import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { api, type TrafficSummary, type ErrorSummary, type LatencySummary, type RoutesSummary, type EgressSummary } from "@/lib/api";
import { ErrorBox, PageHeader, Spinner, StatTile } from "@/components/ui";

type Tab = "traffic" | "errors" | "latency" | "routes" | "egress";
const TABS: { key: Tab; label: string }[] = [
  { key: "traffic", label: "Traffic" },
  { key: "errors", label: "Errors" },
  { key: "latency", label: "Latency" },
  { key: "routes", label: "Routes" },
  { key: "egress", label: "Egress" },
];

export function ObservabilityGatewayPage() {
  const { id = "" } = useParams<{ id: string }>();
  const [tab, setTab] = useState<Tab>("traffic");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [traffic, setTraffic] = useState<TrafficSummary | null>(null);
  const [errors, setErrors] = useState<ErrorSummary | null>(null);
  const [latency, setLatency] = useState<LatencySummary | null>(null);
  const [routes, setRoutes] = useState<RoutesSummary | null>(null);
  const [egress, setEgress] = useState<EgressSummary | null>(null);

  useEffect(() => {
    if (!id) return;
    void (async () => {
      setLoading(true);
      try {
        const [t, e, l, r, eg] = await Promise.all([
          api.gatewayTraffic(id), api.gatewayErrors(id), api.gatewayLatency(id),
          api.gatewayRoutes(id), api.gatewayEgress(id),
        ]);
        setTraffic(t.data); setErrors(e.data);
        setLatency(l.data); setRoutes(r.data); setEgress(eg.data);
      } catch (e) { setError(e); } finally { setLoading(false); }
    })();
  }, [id]);

  if (loading) return <Spinner label="Loading gateway observability…" />;

  return (
    <div>
      <PageHeader eyebrow="Observability" title="API Gateway"
        description="Traffic, errors, latency, routes, and egress for your project's Data API." />
      {error ? <div className="mb-4"><ErrorBox error={error} /></div> : null}
      <nav className="mb-6 flex gap-0 overflow-x-auto border-b border-border-standard">
        {TABS.map((t) => (
          <button key={t.key} type="button" onClick={() => setTab(t.key)}
            className={`-mb-px border-b-2 px-4 py-2.5 text-sm font-medium transition-[color,border-color] duration-fast ease-standard ${tab === t.key ? "border-secondary text-secondary" : "border-transparent text-text-muted hover:text-text-primary"}`}>
            {t.label}
          </button>
        ))}
      </nav>
      {tab === "traffic" && <TrafficTab data={traffic} />}
      {tab === "egress" && <EgressTab data={egress} />}
    </div>
  );
}

function TrafficTab({ data }: { data: TrafficSummary | null }) {
  if (!data) return <Empty label="traffic" />;
  const maxR = useMemo(() => Math.max(1, ...data.time_series.map((b) => b.total_requests)), [data]);
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="Total requests" value={data.total_requests.toLocaleString()} />
        <StatTile label="Req/min" value={data.requests_per_min.toFixed(0)} />
        <StatTile label="Success rate" value={`${data.success_rate.toFixed(1)}%`} />
        <StatTile label="4xx" value={data.by_status_family?.["4xx"] ?? 0} hint={`5xx: ${data.by_status_family?.["5xx"] ?? 0}`} />
      </div>
      <div className="card p-4">
        <h3 className="mb-3 font-sans text-sm font-semibold text-text-primary">Requests over time</h3>
        <ChartBars points={data.time_series.map((b) => ({
          label: new Date(b.bucket_start).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
          value: b.total_requests, max: maxR,
        }))} />
      </div>
      {data.by_route.length > 0 && (
        <div className="card p-4">
          <h3 className="mb-3 font-sans text-sm font-semibold text-text-primary">Top routes</h3>
          <table className="data w-full text-sm"><thead><tr><th>Route</th><th>Method</th><th className="text-right">Calls</th></tr></thead>
            <tbody>{data.by_route.slice(0, 15).map((r, i) => (
              <tr key={i}><td className="font-mono text-xs">{r.route_template}</td><td className="mono-chip">{r.method}</td><td className="text-right tabular-nums">{r.calls.toLocaleString()}</td></tr>
            ))}</tbody></table>
        </div>      )}
    </div>
  );
}

function ErrorsTab({ data }: { data: ErrorSummary | null }) {
  if (!data) return <Empty label="errors" />;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="Total errors" value={data.total_errors.toLocaleString()} />
        <StatTile label="Error rate" value={`${data.error_rate.toFixed(2)}%`} />
        <StatTile label="4xx" value={data.time_series.reduce((a, b) => a + b.count_4xx, 0)} />
        <StatTile label="5xx" value={data.time_series.reduce((a, b) => a + b.count_5xx, 0)} />
      </div>
      {data.top_error_routes.length > 0 && (
        <div className="card p-4">
          <h3 className="mb-3 font-sans text-sm font-semibold text-text-primary">Top error routes</h3>
          <table className="data w-full text-sm"><thead><tr><th>Route</th><th>Method</th><th className="text-right">Errors</th><th className="text-right">Rate</th></tr></thead>
            <tbody>{data.top_error_routes.map((r, i) => (
              <tr key={i}><td className="font-mono text-xs">{r.route_template}</td><td className="mono-chip">{r.method}</td><td className="text-right tabular-nums text-error">{r.error_count}</td><td className="text-right tabular-nums">{r.error_rate.toFixed(1)}%</td></tr>
            ))}</tbody></table>
        </div>
      )}
      {data.recent_errors.length > 0 && (
        <div className="card p-4">
          <h3 className="mb-3 font-sans text-sm font-semibold text-text-primary">Recent errors</h3>
          <div className="max-h-72 overflow-y-auto"><table className="data w-full text-sm"><thead><tr><th>Time</th><th>Route</th><th className="text-right">Status</th><th className="text-right">Dur</th></tr></thead>
            <tbody>{data.recent_errors.map((e) => (
              <tr key={e.event_id}><td className="text-text-muted text-xs">{new Date(e.created_at).toLocaleTimeString()}</td><td className="font-mono text-xs max-w-[160px] truncate">{e.route_template}</td><td className="text-right"><span className="mono-chip text-error">{e.status_code}</span></td><td className="text-right tabular-nums">{e.duration_ms}ms</td></tr>
            ))}</tbody></table></div>
        </div>
      )}
    </div>
  );
}

function LatencyTab({ data }: { data: LatencySummary | null }) {
  if (!data) return <Empty label="latency" />;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-3 gap-3">
        <StatTile label="p50" value={`${data.p50_ms.toFixed(0)}ms`} />
        <StatTile label="p95" value={`${data.p95_ms.toFixed(0)}ms`} />
        <StatTile label="p99" value={`${data.p99_ms.toFixed(0)}ms`} />
      </div>
      {data.slowest_routes.length > 0 && (
        <div className="card p-4">
          <h3 className="mb-3 font-sans text-sm font-semibold text-text-primary">Slowest routes</h3>
          <table className="data w-full text-sm"><thead><tr><th>Route</th><th>Method</th><th className="text-right">Calls</th><th className="text-right">p50</th><th className="text-right">p95</th><th className="text-right">p99</th></tr></thead>
            <tbody>{data.slowest_routes.map((r, i) => (
              <tr key={i}><td className="font-mono text-xs max-w-[180px] truncate">{r.route_template}</td><td className="mono-chip">{r.method}</td><td className="text-right tabular-nums">{r.calls}</td><td className="text-right tabular-nums">{r.p50_ms}ms</td><td className="text-right tabular-nums">{r.p95_ms}ms</td><td className="text-right tabular-nums">{r.p99_ms}ms</td></tr>
            ))}</tbody></table>
        </div>
      )}
    </div>
  );
}

function RoutesTab({ data }: { data: RoutesSummary | null }) {
  if (!data) return <Empty label="routes" />;
  return (
    <div className="card p-4">
      <h3 className="mb-3 font-sans text-sm font-semibold text-text-primary">All routes</h3>
      <div className="max-h-[450px] overflow-y-auto"><table className="data w-full text-sm"><thead><tr><th>Route</th><th>Method</th><th className="text-right">Calls</th><th className="text-right">Err%</th><th className="text-right">p95</th><th className="text-right">Bytes out</th></tr></thead>
        <tbody>{data.routes.map((r, i) => (
          <tr key={i}><td className="font-mono text-xs max-w-[180px] truncate">{r.route_template}</td><td className="mono-chip">{r.method}</td><td className="text-right tabular-nums">{r.calls.toLocaleString()}</td><td className="text-right tabular-nums">{r.error_rate}%</td><td className="text-right tabular-nums">{r.p95_ms}ms</td><td className="text-right tabular-nums text-text-muted">{fmtBytes(r.bytes_out)}</td></tr>
        ))}</tbody></table></div>
    </div>
  );
}

function EgressTab({ data }: { data: EgressSummary | null }) {
  if (!data) return <Empty label="egress" />;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <StatTile label="Total ingress" value={fmtBytes(data.total_ingress_bytes)} />
        <StatTile label="Total egress" value={fmtBytes(data.total_egress_bytes)} />
        <StatTile label="Avg response" value={fmtBytes(Math.round(data.avg_response_size))} />
      </div>
      {data.top_egress_routes.length > 0 && (
        <div className="card p-4">
          <h3 className="mb-3 font-sans text-sm font-semibold text-text-primary">Top egress routes</h3>
          <table className="data w-full text-sm"><thead><tr><th>Route</th><th>Method</th><th className="text-right">Calls</th><th className="text-right">Egress</th><th className="text-right">Avg</th></tr></thead>
            <tbody>{data.top_egress_routes.map((r, i) => (
              <tr key={i}><td className="font-mono text-xs">{r.route_template}</td><td className="mono-chip">{r.method}</td><td className="text-right tabular-nums">{r.calls}</td><td className="text-right tabular-nums">{fmtBytes(r.total_egress_bytes)}</td><td className="text-right tabular-nums text-text-muted">{fmtBytes(r.avg_response_bytes)}</td></tr>
            ))}</tbody></table>
        </div>
      )}
    </div>
  );
}

function Empty({ label }: { label: string }) {
  return <div className="card px-6 py-12 text-center"><p className="text-sm text-text-muted">No {label} data yet. Telemetry appears as your API processes requests.</p></div>;
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1048576) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1048576).toFixed(1)} MB`;
}

function ChartBars({ points }: { points: { label: string; value: number; max: number }[] }) {
  if (points.length === 0) return <p className="text-sm text-text-muted">No data</p>;
  return (
    <div className="flex h-32 items-end gap-px rounded-lg border border-border-standard bg-surface-container-low/80 px-1 pb-1 pt-2">
      {points.map((p, i) => {
        const h = Math.max(4, Math.round((p.value / p.max) * 100));
        return <div key={i} className="group relative flex-1 rounded-t-sm bg-primary/70 transition-colors duration-fast ease-standard hover:bg-primary" style={{ height: `${h}%` }} title={`${p.label}: ${p.value}`}>
          <span className="pointer-events-none absolute -top-6 left-1/2 z-tooltip hidden -translate-x-1/2 whitespace-nowrap rounded-sm bg-inverse-surface px-1.5 py-0.5 font-mono text-[10px] text-inverse-on-surface group-hover:block">{p.value}</span>
        </div>;
      })}
    </div>
  );
}


